import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiPlus, FiMoreVertical, FiEdit2, FiLink, FiTrash2, FiSettings, FiUser } = FiIcons;

export default function Sidebar({
  activeRoom,
  setActiveRoom,
  rooms,
  onAddRoom,
  onDeleteRoom,
  onEditRoom,
}) {
  const [openMenu, setOpenMenu]         = useState(null);
  const [userPopover, setUserPopover]   = useState(false);
  const [userEmail, setUserEmail]       = useState('operator@studio.com');
  const menuRef                         = useRef(null);
  const desktopPopoverRef               = useRef(null);
  const mobilePopoverRef                = useRef(null);

  // Email Update States
  const [isEditingEmail, setIsEditingEmail]     = useState(false);
  const [newEmail, setNewEmail]                 = useState('');
  const [emailUpdateSent, setEmailUpdateSent]   = useState(false);
  const [isUpdating, setIsUpdating]             = useState(false);
  const [emailUpdateError, setEmailUpdateError] = useState('');

  // Password Update States
  const [isEditingPassword, setIsEditingPassword]         = useState(false);
  const [newPassword, setNewPassword]                     = useState('');
  const [passwordUpdated, setPasswordUpdated]             = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword]       = useState(false);
  const [passwordUpdateError, setPasswordUpdateError]     = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) setUserEmail(user.email);
    });

    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const inDesktop = desktopPopoverRef.current?.contains(e.target);
      const inMobile = mobilePopoverRef.current?.contains(e.target);
      
      if (!inDesktop && !inMobile) {
        setUserPopover(false);
        
        setIsEditingEmail(false);
        setEmailUpdateSent(false);
        setNewEmail('');
        setEmailUpdateError('');
        
        setIsEditingPassword(false);
        setPasswordUpdated(false);
        setNewPassword('');
        setPasswordUpdateError('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCopyUrl = (room) => {
    const url = `${window.location.origin}/#/display/${room.id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setOpenMenu(null);
  };

  const handleSelectRoom = (room) => {
    localStorage.setItem('selected_room_id', room.id);
    setActiveRoom(room);
  };

  const handleSignOut = async () => {
    setUserPopover(false);
    await supabase.auth.signOut();
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || isUpdating) return;
    setIsUpdating(true);
    setEmailUpdateError('');
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail }, {
        emailRedirectTo: window.location.origin + window.location.pathname
      });
      if (error) throw error;
      setEmailUpdateSent(true);
      setNewEmail('');
    } catch (err) {
      console.error(err);
      setEmailUpdateError(err.message || 'Failed to update email.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || isUpdatingPassword) return;
    setIsUpdatingPassword(true);
    setPasswordUpdateError('');
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordUpdated(true);
      setNewPassword(''); 
    } catch (err) {
      console.error(err);
      setPasswordUpdateError(err.message || 'Failed to update password.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleAccessibilityClick = (e) => {
    e.preventDefault();
    setUserPopover(false);
    document.querySelector('.asw-menu-btn')?.click();
  };

  const renderUserSettingsBlock = () => {
    if (emailUpdateSent) {
      return (
        <div className="px-4 py-5 text-center flex flex-col items-center">
          <h3 className="font-semibold text-[#FFFFFF] mb-2 text-sm">Verify new email</h3>
          <p className="text-[#A1A1AA] text-[11px] leading-[1.6]">
            A confirmation link has been sent. Please verify the link to complete your account update.
          </p>
        </div>
      );
    }
    
    if (passwordUpdated) {
      return (
        <div className="px-4 py-5 text-center flex flex-col items-center">
          <h3 className="font-semibold text-[#FFFFFF] mb-2 text-sm">Password updated</h3>
          <p className="text-[#A1A1AA] text-[11px] leading-[1.6]">
            Your secure account access password has been updated successfully.
          </p>
        </div>
      );
    }

    if (isEditingEmail) {
      return (
        <form onSubmit={handleUpdateEmail} className="px-4 py-3 flex flex-col gap-2.5">
          {emailUpdateError && (
            <div className="text-[10px] text-[#d95555] font-medium leading-tight mb-1 text-center bg-[#1a0f0f] p-1.5 rounded border border-[#2a1111]">
              {emailUpdateError}
            </div>
          )}
          <input
            type="email"
            required
            placeholder="New Email Address"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value); setEmailUpdateError(''); }}
            className="w-full bg-[#0a0a0a] border border-[#222222] rounded-lg px-3 py-2 text-xs font-medium text-white placeholder-[#555555] focus:outline-none focus:border-white transition-colors"
            autoFocus
          />
          <div className="flex gap-2 w-full">
            <button
              type="button"
              onClick={() => {
                setIsEditingEmail(false);
                setNewEmail('');
                setEmailUpdateError('');
              }}
              className="flex-1 px-3 py-2 text-xs font-medium text-[#aaaaaa] hover:text-white bg-[#1a1a1a] rounded-lg transition-colors border border-transparent hover:border-[#333333]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="flex-1 px-3 py-2 text-xs font-semibold text-black bg-white rounded-lg hover:bg-[#e0e0e0] transition-colors disabled:opacity-50 border border-white"
            >
              {isUpdating ? 'Sending...' : 'Update'}
            </button>
          </div>
        </form>
      );
    }

    if (isEditingPassword) {
      return (
        <form onSubmit={handleUpdatePassword} className="px-4 py-3 flex flex-col gap-2.5">
          {passwordUpdateError && (
            <div className="text-[10px] text-[#d95555] font-medium leading-tight mb-1 text-center bg-[#1a0f0f] p-1.5 rounded border border-[#2a1111]">
              {passwordUpdateError}
            </div>
          )}
          <input
            type="password"
            required
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setPasswordUpdateError(''); }}
            className="w-full bg-[#0a0a0a] border border-[#222222] rounded-lg px-3 py-2 text-xs font-medium text-white placeholder-[#555555] focus:outline-none focus:border-white transition-colors"
            autoFocus
          />
          <div className="flex gap-2 w-full">
            <button
              type="button"
              onClick={() => {
                setIsEditingPassword(false);
                setNewPassword('');
                setPasswordUpdateError('');
              }}
              className="flex-1 px-3 py-2 text-xs font-medium text-[#aaaaaa] hover:text-white bg-[#1a1a1a] rounded-lg transition-colors border border-transparent hover:border-[#333333]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdatingPassword}
              className="flex-1 px-3 py-2 text-xs font-semibold text-black bg-white rounded-lg hover:bg-[#e0e0e0] transition-colors disabled:opacity-50 border border-white"
            >
              {isUpdatingPassword ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      );
    }

    return (
      <div className="flex flex-col py-1.5">
        <button 
          onClick={() => setIsEditingEmail(true)} 
          className="w-full text-center px-4 py-2.5 text-xs font-medium text-[#aaaaaa] hover:text-white hover:bg-[#1c1c1c] transition-colors duration-100"
        >
          Update Email
        </button>
        <button 
          onClick={() => setIsEditingPassword(true)}
          className="w-full text-center px-4 py-2.5 text-xs font-medium text-[#aaaaaa] hover:text-white hover:bg-[#1c1c1c] transition-colors duration-100"
        >
          Update Password
        </button>
      </div>
    );
  };

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="hidden md:flex w-64 h-screen bg-[#0d0d0d] border-r border-[#1e1e1e] flex-col shrink-0">
        
        {/* Zone 1: The Top Header (Pinned) */}
        <div className="shrink-0 px-4 pt-8 pb-5 border-b border-[#1a1a1a]">
          <div className="px-3 mb-6">
            <h1 className="font-extrabold text-[0.78rem] tracking-[0.28em] text-white uppercase">
              StudioTimer
            </h1>
          </div>
          <button
            onClick={onAddRoom}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-muted hover:text-white text-sm font-medium transition-all duration-150 hover:bg-[#161616]"
          >
            <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
            <span>Add Room</span>
          </button>
        </div>

        {/* Zone 2: The Room Runway (Infinite Scroll) */}
        <nav className="flex-1 overflow-y-auto scrollbar-hide px-3 py-4" ref={menuRef}>
          <p className="px-4 text-[0.65rem] font-semibold text-muted tracking-[0.2em] uppercase mb-3">
            Rooms
          </p>
          <ul className="space-y-0.5">
            {rooms.map((room) => {
              const isActive   = activeRoom?.id === room.id;
              const isMenuOpen = openMenu   === room.id;
              return (
                <li key={room.id} className="relative">
                  <div
                    className={`flex items-center rounded-lg transition-all duration-150 ${
                      isActive ? 'bg-[#1e1e1e]' : 'hover:bg-[#161616]'
                    }`}
                  >
                    <button
                      onClick={() => handleSelectRoom(room)}
                      className={`flex-1 text-left px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                        isActive ? 'text-white' : 'text-muted hover:text-white'
                      }`}
                    >
                      {room.name}
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenu(isMenuOpen ? null : room.id);
                      }}
                      aria-label="Room options"
                      style={{ opacity: isActive || isMenuOpen ? 1 : 0 }}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-[#444444] hover:text-white hover:bg-[#2a2a2a] transition-all duration-150 focus:opacity-100"
                    >
                      <SafeIcon icon={FiMoreVertical} className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {isMenuOpen && (
                    <div
                      className="absolute right-0 top-full mt-1 z-40 w-52 rounded-xl border border-[#272727] overflow-hidden"
                      style={{ backgroundColor: '#131313', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}
                    >
                      <ContextMenuItem
                        icon={FiEdit2}
                        label="Edit Room"
                        onClick={() => {
                          setOpenMenu(null);
                          onEditRoom(room);
                        }}
                      />
                      <ContextMenuItem
                        icon={FiLink}
                        label="Copy Display URL"
                        onClick={() => handleCopyUrl(room)}
                      />
                      <div className="h-px bg-[#202020] mx-3 my-0.5" />
                      <ContextMenuItem
                        icon={FiTrash2}
                        label="Delete Room"
                        destructive
                        onClick={() => {
                          setOpenMenu(null);
                          onDeleteRoom(room);
                        }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Zone 3: The Bottom Footer (Pinned) */}
        <div className="shrink-0 py-4 border-t border-[#1a1a1a] flex justify-center relative" ref={desktopPopoverRef}>
          <button
            onClick={() => setUserPopover((v) => !v)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#111111] hover:bg-[#1a1a1a] border border-[#222222] text-[#888888] hover:text-white transition-all duration-150 outline-none"
            aria-label="Account Settings"
          >
            <SafeIcon icon={FiUser} className="w-4 h-4" />
            <span className="text-xs font-semibold tracking-wide">Settings</span>
          </button>

          {userPopover && (
            <div
              className="absolute bottom-[calc(100%+0.5rem)] left-1/2 -translate-x-1/2 w-[260px] rounded-xl border border-[#252525] overflow-hidden z-50 flex flex-col"
              style={{ backgroundColor: '#111111', boxShadow: '0 -8px 32px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3)' }}
            >
              {/* Popover Header: Dynamic User Email */}
              <div className="px-4 py-3.5 border-b border-[#1e1e1e] text-center cursor-default">
                <span className="block text-[11px] text-[#666666] font-medium tracking-wide truncate">
                  {userEmail}
                </span>
              </div>

              {/* Interactive Options (Update Email / Reset Password) */}
              {renderUserSettingsBlock()}

              <div className="h-px bg-[#1e1e1e] mx-4 my-1" />

              {/* Utility Footer */}
              <div className="flex flex-wrap justify-center items-center gap-1.5 py-3 px-2">
                <a href="mailto:support@help.studiotimer.app" className="text-[9px] text-[#555555] hover:text-[#aaaaaa] transition-colors uppercase tracking-wider">Support</a>
                <span className="text-[#333333] text-[9px]">•</span>
                <a href="https://gettermscdn.com/view/K4uXe/privacy/en-us" target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#555555] hover:text-[#aaaaaa] transition-colors uppercase tracking-wider">Privacy</a>
                <span className="text-[#333333] text-[9px]">•</span>
                <a href="https://gettermscdn.com/view/K4uXe/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#555555] hover:text-[#aaaaaa] transition-colors uppercase tracking-wider">Terms</a>
                <span className="text-[#333333] text-[9px]">•</span>
                <button onClick={handleAccessibilityClick} className="text-[9px] text-[#555555] hover:text-[#aaaaaa] transition-colors uppercase tracking-wider outline-none">Accessibility</button>
              </div>

              {/* The Exit - Hooked up to Supabase SignOut */}
              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center px-4 py-3.5 text-xs font-semibold transition-colors duration-150 bg-[#0a0a0a] border-t border-[#1e1e1e] hover:bg-[#1a1010]"
                style={{ color: '#b84444' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#d95555'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#b84444'; }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── MOBILE TOP BAR ── */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-[#1e1e1e]">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h1 className="font-extrabold text-[0.7rem] tracking-[0.28em] text-white uppercase">
            StudioTimer
          </h1>
          <div className="flex items-center gap-2">
            <button
              ref={mobilePopoverRef}
              onClick={() => setUserPopover((v) => !v)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white opacity-50 hover:opacity-100 transition-opacity relative"
            >
              <SafeIcon icon={FiSettings} className="w-4 h-4" />
              {/* Mobile Popover */}
              {userPopover && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-full right-0 mt-2 w-[240px] rounded-xl border border-[#252525] overflow-hidden z-50 flex flex-col"
                  style={{ backgroundColor: '#111111', boxShadow: '0 8px 32px rgba(0,0,0,0.55)' }}
                >
                  <div className="px-4 py-3 border-b border-[#1e1e1e] text-center cursor-default">
                    <span className="block text-[10px] text-[#666666] font-medium tracking-wide truncate mt-1">
                      {userEmail}
                    </span>
                  </div>
                  
                  {/* Interactive Options (Update Email / Reset Password) */}
                  {renderUserSettingsBlock()}

                  <div className="h-px bg-[#1e1e1e] mx-4 my-1" />
                  
                  {/* Utility Footer */}
                  <div className="flex flex-wrap justify-center items-center gap-1.5 py-3 px-2">
                    <a href="mailto:support@help.studiotimer.app" className="text-[8px] text-[#555555] hover:text-[#aaaaaa] uppercase tracking-wider">Support</a>
                    <span className="text-[#333333] text-[8px]">•</span>
                    <a href="https://gettermscdn.com/view/K4uXe/privacy/en-us" target="_blank" rel="noopener noreferrer" className="text-[8px] text-[#555555] hover:text-[#aaaaaa] uppercase tracking-wider">Privacy</a>
                    <span className="text-[#333333] text-[8px]">•</span>
                    <a href="https://gettermscdn.com/view/K4uXe/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-[8px] text-[#555555] hover:text-[#aaaaaa] uppercase tracking-wider">Terms</a>
                    <span className="text-[#333333] text-[8px]">•</span>
                    <button onClick={handleAccessibilityClick} className="text-[8px] text-[#555555] hover:text-[#aaaaaa] uppercase tracking-wider outline-none">Accessibility</button>
                  </div>
                  
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center px-4 py-3.5 text-xs font-semibold bg-[#0a0a0a] border-t border-[#1e1e1e] text-[#b84444] active:bg-[#1a1010]"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </button>
            <button
              onClick={onAddRoom}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted hover:text-white transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="mobile-room-scroll flex gap-2 overflow-x-auto px-5 pb-3 pr-5 pt-1">
          {rooms.map((room) => {
            const isActive = activeRoom?.id === room.id;
            return (
              <button
                key={room.id}
                onClick={() => handleSelectRoom(room)}
                className={`shrink-0 min-h-[44px] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-150 border ${
                  isActive
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-muted border-[#2a2a2a] hover:text-white'
                }`}
              >
                {room.name}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ContextMenuItem({ icon, label, destructive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-medium transition-colors duration-100 ${
        destructive
          ? 'text-[#c94444] hover:bg-[#1c1010]'
          : 'text-[#aaaaaa] hover:text-white hover:bg-[#1e1e1e]'
      }`}
    >
      <SafeIcon icon={icon} className="w-3.5 h-3.5 shrink-0" />
      {label}
    </button>
  );
}